import React, { useRef, useEffect, useMemo, useState, forwardRef, useImperativeHandle  } from 'react';
import { 
  Modal, 
  Box, 
  Typography, 
  Button, 
  DialogContentText, 
  Select, 
  MenuItem,  
  FormControl, 
  InputLabel, 
  FormHelperText, 
  InputAdornment, 
  IconButton, 
  TextField,
  Checkbox,
  ListItemText,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import ErrorIcon from '@mui/icons-material/Error';
import './modalstyles.css';
import '../../../styles/common.scss';
import { Visibility, VisibilityOff } from "@mui/icons-material";
import ButtonComponent from '../../customButton';

const ModalWrapper = forwardRef(({ open, onClose, title, children, buttons, fields, onSubmit, containerStyles = {}, initialValues = {}, loading = false, customButtons = [] },ref) => {
  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
    getValues
  } = useForm();

  // Enhanced submit handler that prevents form reset during loading
  const handleFormSubmit = (data) => {
    // Don't reset form during submission - let parent handle success/error
    return onSubmit(data);
  };

  const defaultValues = useMemo(() => {
    return fields.reduce((acc, field) => {
      // For multiselect fields, ensure default value is an array
      if (field.Component === Select && field.multiple) {
        acc[field.name] = initialValues[field.name] || field.defaultValue || [];
      } else {
        acc[field.name] = initialValues[field.name] ?? field.defaultValue ?? '';
      }
      return acc;
    }, {});
  }, [fields, initialValues]);

  // Only reset the form when the modal transitions from closed to open
  const prevOpen = useRef(false);
  useEffect(() => {
    if (open && !prevOpen.current) {
      reset(defaultValues);
    }
    prevOpen.current = open;
  }, [open, reset, defaultValues]);

  //Update values for dependendent fields
  useImperativeHandle(ref, () => ({
    resetForm: (values) => {
      reset(values);
    },
    getValues: () => getValues()
  }));

  const inputRefs = useRef({});
  const [showPassword, setShowPassword] = useState({});

  const togglePasswordVisibility = (name) => {
    setShowPassword(prev => ({
      ...prev,
      [name]: !prev[name]
    }));
  };

  const handleKeyPress = (event) => {
    
    if (event.key === "Enter" && event.shiftKey) {
      return; 
    }
  
    if (event.key === "Enter") {
      const activeElement = document.activeElement;
  
      // If the active element is a textarea or input with multiline attribute, ignore Enter
      if (activeElement && activeElement.tagName === 'TEXTAREA') {
        return; 
      }
  
      const defaultButton = buttons.find((button) => button.isdefault);
      if (defaultButton && defaultButton.onClick) {
        defaultButton.onClick();
      } else {
        handleSubmit(handleFormSubmit)();
      }
    }
  };
  
  useEffect(() => {
    if (open && !loading) {
      // Only reset and focus when modal opens and not during loading
      setTimeout(() => {
        fields.forEach((field) => {
          if (field.autoFocus && inputRefs.current[field.name]) {
            inputRefs.current[field.name].focus();
          }
        });
      }, 1);
    }
  }, [fields, open, loading]);

  // Check if there are any errors
  const hasError = Object.keys(errors).length > 0;

  // Helper function for rendering field components
  const renderField = (field, index) => {
    const { 
      Component, 
      name, 
      customOnChange, 
      required = false, 
      autoFocus = false, 
      inputLabel, 
      options = [], 
      rows = 0, 
      multiline = false, 
      multiple = false, 
      disabled = false,
      rules = {}, 
      margin = "normal",
      ...props 
    } = field;

    if (Component === DialogContentText) {
      return (
        <Component key={name || index} className="modal-dialog-content-text" {...props}>
          {field.children}
        </Component>
      );
    }

    const validationRules = {
      ...rules,
      ...(required && { required: `${inputLabel || field.label || name} is required.` }),
    };

    const [size, setSize] = useState("small");

    useEffect(() => {
      const handleResize = () => {
        if(typeof window !== "undefined") {
          setSize(window.innerWidth >= 1536 ? "medium" : "small");
        }
      }
      window.addEventListener('resize', handleResize);
      handleResize();
      return () => window.removeEventListener('resize', handleResize);
    }, []);
    
    return (
      <Controller
        key={index}
        name={name}
        control={control}
        rules={validationRules}
        render={({ field: { onChange, value, ...restField } }) => {
          const isPasswordField = props.type && props.type === "password";

          if (Component === Select) {
            return (
              <FormControl 
                fullWidth 
                size={size}
                margin={margin}
                error={!!errors[name]} 
                required={required} 
              >
                <InputLabel id={`${name}-label`}>{inputLabel}</InputLabel>
                <Component
                  labelId={`${name}-label`}
                  id={name}
                  value={value}
                  label={inputLabel}
                  multiple={multiple}
                  size={size}
                  disabled={disabled || loading}
                  onChange={(e) => {
                    onChange(e);
                    if (customOnChange) {
                      customOnChange(name, e.target.value, getValues());
                    }
                  }}
                  renderValue={multiple ? 
                    (selected) => {
                      if (selected.length === 0) {
                        return <em>No options available</em>;
                      }
                      return selected
                        .map(val => options.find(opt => opt.value === val)?.label || val)
                        .join(', ');
                    } : 
                    (selected) => {
                      if (options.length === 0) {
                        return <em>No {inputLabel?.toLowerCase() || 'option'} available</em>;
                      }
                      if (!selected) {
                        return '';
                      }
                      return options.find(opt => opt.value === selected)?.label || selected;
                    }
                  }

                  {...props}
                  margin={margin}
                >
                  {multiple && options.length === 0 && <MenuItem disabled value=""><em>No options available</em></MenuItem>}
                  {!multiple && options.length === 0 && <MenuItem disabled value=""><em>No {inputLabel?.toLowerCase() || 'option'} available</em></MenuItem>}
                  {options.map((option) => (
                    <MenuItem 
                      key={option.value} 
                      value={option.value}
                      sx={{ fontWeight: !multiple && value === option.value ? "bold" : "normal" }}
                    >
                      {multiple && <Checkbox checked={Array.isArray(value) ? value.indexOf(option.value) > -1 : false} />}
                      <ListItemText primary={option.label} />
                    </MenuItem>
                  ))}
                </Component>
                <FormHelperText className="modal-helper-text">{errors[name]?.message}</FormHelperText>
              </FormControl>
            );
          }
          
          return (
            <Component
              value={value}
              size={size}
              margin={margin}
              onChange={(e) => {
                onChange(e);
                if (customOnChange) {
                  customOnChange(name, e.target.value, getValues());
                }
              }}
              {...restField}
              {...props}
              rows={rows}
              inputRef={(ref) => {
                inputRefs.current[name] = ref;
              }}
              multiline={multiline}
              disabled={disabled || loading}
              required={required}
              error={!!errors[name]}
              helperText={errors[name]?.message || ""}
              FormHelperTextProps={{
                className: "modal-helper-text"
              }}
              type={isPasswordField ? (showPassword[name] ? "text" : "password") : props.type}
              autoComplete={isPasswordField ? "new-password" : "off"}  
              InputProps={
                isPasswordField
                  ? {
                      endAdornment: (
                        name !== 'apiKey' ? (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => togglePasswordVisibility(name)}
                              edge="end"
                            >
                              {showPassword[name] ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ) : null
                      ),
                    }
                  : props.InputProps
              }
            />
          );
        }}
      />
    );
  };

  return (
    <Modal
      open={open}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          onClose();
        }
      }}
      onKeyDown={handleKeyPress}
    >
      <Box className="modal-container" sx={{ ...containerStyles }}>
        <Box className="modal-header" display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="componentTitle">{title}</Typography>
          {hasError && (
            <Box display="flex" alignItems="center">
              <ErrorIcon color="error" />
            </Box>
          )}
        </Box>

        <Box className="modal-body">
          {/* Render custom buttons above fields */}
          {customButtons.length > 0 && (
            <Box sx={{ mb: 0 }}>
              {customButtons.map((customButton, index) => (
                <Button
                  key={index}
                  variant={customButton.variant || 'outlined'}
                  color={customButton.color || 'primary'}
                  size={customButton.size || 'small'}
                  onClick={customButton.onClick}
                  disabled={customButton.disabled || loading}
                  startIcon={customButton.icon}
                  sx={{ mr: 1, mb: 1, ...customButton.sx }}
                >
                  {customButton.label}
                </Button>
              ))}
            </Box>
          )}
          {fields?.map((field, index) => renderField(field, index))}
          {children}
        </Box>

        <Box className="modal-buttons">
          {buttons
            ? buttons.map((button, index) => (
                <ButtonComponent
                  key={index}
                  variant={button.variant}                  
                  onClick={button.onClick || handleSubmit(handleFormSubmit)}
                  disabled={button.disabled || loading}
                  className={button.className}
                  ctaText={button.label}
                />
              ))
            : (
                <Button variant="contained" onClick={onClose}>
                  Cancel
                </Button>
              )}
        </Box>
      </Box>
    </Modal>
  );
});

export default ModalWrapper;